import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface DataPoint {
  label: string;
  value: number;
  unit: string;
  color: string;
}

interface ComparisonItem {
  label: string;
  icon: string;
  value: string;
}

interface SciencePopularizationProps {
  hookQuestion: string;
  topic: string;
  subtitle: string;
  bigNumber: { value: number; unit: string; description: string };
  dataPoints: DataPoint[];
  coreExplanation: string;
  comparison?: { left: ComparisonItem; right: ComparisonItem; vs: string };
  source: string;
}

const DEFAULT_PROPS: SciencePopularizationProps = {
  hookQuestion: '你知道吗？',
  topic: '人类大脑的惊人算力',
  subtitle: 'The Computing Power of Human Brain',
  bigNumber: {
    value: 1000000000000000,
    unit: '次/秒',
    description: '大脑每秒可进行的突触运算次数',
  },
  dataPoints: [
    { label: '神经元', value: 860, unit: '亿个', color: '#f472b6' },
    { label: '突触连接', value: 100, unit: '万亿', color: '#818cf8' },
    { label: '信号速度', value: 120, unit: 'm/s', color: '#34d399' },
    { label: '能耗', value: 20, unit: '瓦特', color: '#fbbf24' },
  ],
  coreExplanation: '人脑仅用 20 瓦能耗（相当于一个灯泡），就实现了超级计算机数千万瓦才能达到的计算效率',
  comparison: {
    left: { label: '人脑', icon: '🧠', value: '20W' },
    right: { label: '超算', icon: '🖥️', value: '20MW' },
    vs: '效率差 100 万倍',
  },
  source: 'Nature Neuroscience, 2024',
};

const AnimatedNumber: React.FC<{ value: number; frame: number; startFrame: number; duration: number; fontSize: number }> = ({
  value,
  frame,
  startFrame,
  duration,
  fontSize,
}) => {
  const progress = interpolate(frame, [startFrame, startFrame + duration], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const eased = 1 - Math.pow(1 - progress, 3);
  const current = Math.floor(value * eased);

  let displayValue: string;
  if (value >= 1e15) {
    displayValue = current >= 1e15 ? '10¹⁵' : current.toExponential(0);
  } else if (value >= 1e9) {
    displayValue = (current / 1e9).toFixed(0) + 'B';
  } else if (value >= 1e6) {
    displayValue = (current / 1e6).toFixed(0) + 'M';
  } else {
    displayValue = current.toLocaleString();
  }

  return <span style={{ fontSize, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{displayValue}</span>;
};

export const SciencePopularizationTemplate: React.FC<Partial<SciencePopularizationProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Phase 1: Hook (0-60)
  const hookScale = spring({ frame, fps, from: 0.5, to: 1, config: { damping: 10, stiffness: 100, mass: 0.8 } });
  const hookOpacity = interpolate(frame, [0, 15], [0, 1], { extrapolateRight: 'clamp' });
  const hookFade = interpolate(frame, [50, 65], [1, 0], { extrapolateRight: 'clamp' });

  // Phase 2: Topic (60-120)
  const topicOpacity = interpolate(frame, [60, 85], [0, 1], { extrapolateRight: 'clamp' });
  const topicY = spring({ frame: frame - 60, fps, from: 40, to: 0, config: { damping: 18, stiffness: 100 } });

  // Phase 3: Big number (120-220)
  const bigNumOpacity = interpolate(frame, [120, 145], [0, 1], { extrapolateRight: 'clamp' });

  // Phase 4: Data grid (220-360)
  // Phase 5: Core explanation (360-420)
  const explanationOpacity = interpolate(frame, [360, 390], [0, 1], { extrapolateRight: 'clamp' });
  const explanationY = spring({ frame: frame - 360, fps, from: 30, to: 0, config: { damping: 20, stiffness: 100 } });

  // Phase 6: Comparison (420-500)
  const comparisonOpacity = interpolate(frame, [420, 450], [0, 1], { extrapolateRight: 'clamp' });

  // Pulsing glow
  const glowPulse = interpolate(Math.sin(frame / 15), [-1, 1], [0.4, 0.8]);

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #020617 0%, #0f172a 40%, #1e1b4b 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Helvetica Neue", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Decorative circles */}
      <div
        style={{
          position: 'absolute',
          top: -200,
          right: -200,
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(129, 140, 248, ${glowPulse * 0.15}), transparent 70%)`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(244, 114, 182, ${glowPulse * 0.12}), transparent 70%)`,
        }}
      />

      {/* Hook question */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          opacity: hookOpacity * hookFade,
          transform: `scale(${hookScale})`,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 80, fontWeight: 800, color: '#fbbf24' }}>
            {p.hookQuestion}
          </div>
          <div
            style={{
              fontSize: 32,
              marginTop: 20,
              color: 'rgba(255,255,255,0.5)',
            }}
          >
            滑动了解 →
          </div>
        </div>
      </div>

      {/* Topic title */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: topicOpacity,
          transform: `translateY(${topicY}px)`,
        }}
      >
        <div style={{ fontSize: 40, color: '#818cf8', fontWeight: 600, letterSpacing: '0.1em', marginBottom: 12 }}>
          硬核科普
        </div>
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: '0 60px',
            lineHeight: 1.3,
            background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe, #a5b4fc)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {p.topic}
        </h1>
        <p style={{ fontSize: 36, marginTop: 12, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
          {p.subtitle}
        </p>
      </div>

      {/* Big number showcase */}
      <div
        style={{
          position: 'absolute',
          top: 340,
          left: 60,
          right: 60,
          textAlign: 'center',
          opacity: bigNumOpacity,
        }}
      >
        <div
          style={{
            background: 'rgba(129, 140, 248, 0.08)',
            border: '1px solid rgba(129, 140, 248, 0.2)',
            borderRadius: 28,
            padding: '40px 40px 32px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', color: '#818cf8' }}>
            <AnimatedNumber value={p.bigNumber.value} frame={frame} startFrame={130} duration={60} fontSize={72} />
            <span style={{ fontSize: 40, color: '#c7d2fe', marginLeft: 8 }}>{p.bigNumber.unit}</span>
          </div>
          <div style={{ fontSize: 36, color: 'rgba(255,255,255,0.5)', marginTop: 16 }}>
            {p.bigNumber.description}
          </div>
        </div>
      </div>

      {/* Data points grid */}
      <div
        style={{
          position: 'absolute',
          top: 660,
          left: 60,
          right: 60,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
        }}
      >
        {p.dataPoints.map((dp, i) => {
          const dpStart = 220 + i * 30;
          const dpProgress = spring({
            frame: frame - dpStart,
            fps,
            from: 0,
            to: 1,
            config: { damping: 14, stiffness: 120, mass: 0.6 },
          });

          return (
            <div
              key={i}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${dp.color}30`,
                borderRadius: 20,
                padding: '24px 24px',
                opacity: dpProgress,
                transform: `scale(${0.9 + dpProgress * 0.1})`,
                textAlign: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', color: dp.color }}>
                <AnimatedNumber value={dp.value} frame={frame} startFrame={dpStart} duration={40} fontSize={44} />
                <span style={{ fontSize: 42, opacity: 0.8, marginLeft: 4 }}>{dp.unit}</span>
              </div>
              <div style={{ fontSize: 36, color: 'rgba(255,255,255,0.6)', marginTop: 8 }}>{dp.label}</div>
            </div>
          );
        })}
      </div>

      {/* Core explanation */}
      <div
        style={{
          position: 'absolute',
          top: 1080,
          left: 60,
          right: 60,
          opacity: explanationOpacity,
          transform: `translateY(${explanationY}px)`,
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.08), rgba(249, 115, 22, 0.05))',
            border: '1px solid rgba(251, 191, 36, 0.2)',
            borderRadius: 20,
            padding: '28px 32px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <span style={{ fontSize: 36, flexShrink: 0 }}>🔬</span>
            <span style={{ fontSize: 36, lineHeight: 1.6, color: 'rgba(255,255,255,0.85)' }}>
              {p.coreExplanation}
            </span>
          </div>
        </div>
      </div>

      {/* Comparison */}
      {p.comparison && (
        <div
          style={{
            position: 'absolute',
            top: 1340,
            left: 60,
            right: 60,
            opacity: comparisonOpacity,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Left */}
            <div
              style={{
                flex: 1,
                background: 'rgba(52, 211, 153, 0.08)',
                border: '1px solid rgba(52, 211, 153, 0.25)',
                borderRadius: 20,
                padding: '28px 10px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 52 }}>{p.comparison.left.icon}</div>
              <div style={{ fontSize: 42, fontWeight: 700, marginTop: 8 }}>{p.comparison.left.label}</div>
              <div style={{ fontSize: 40, fontWeight: 800, color: '#34d399', marginTop: 8 }}>
                {p.comparison.left.value}
              </div>
            </div>

            {/* VS */}
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: '#fbbf24',
                  background: 'rgba(251, 191, 36, 0.15)',
                  borderRadius: 12,
                  padding: '8px 16px',
                }}
              >
                VS
              </div>
              <div style={{ fontSize: 32, color: 'rgba(255,255,255,0.5)', marginTop: 8, maxWidth: 300 }}>
                {p.comparison.vs}
              </div>
            </div>

            {/* Right */}
            <div
              style={{
                flex: 1,
                background: 'rgba(244, 114, 182, 0.08)',
                border: '1px solid rgba(244, 114, 182, 0.25)',
                borderRadius: 20,
                padding: '28px 10px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 52 }}>{p.comparison.right.icon}</div>
              <div style={{ fontSize: 42, fontWeight: 700, marginTop: 8 }}>{p.comparison.right.label}</div>
              <div style={{ fontSize: 40, fontWeight: 800, color: '#f472b6', marginTop: 8 }}>
                {p.comparison.right.value}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Source */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: interpolate(frame, [480, 510], [0, 1], { extrapolateRight: 'clamp' }),
        }}
      >
        <span style={{ fontSize: 42, color: 'rgba(255,255,255,0.3)' }}>📎 {p.source}</span>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #818cf8, #f472b6)',
        }}
      />
    </AbsoluteFill>
  );
};
